import { useState, useEffect } from "react";
import axios from "axios";
import { Sidebar } from "../components/layout/Sidebar";
import { useSidebar } from "../contexts/SidebarContext";
import { Button } from "../components/ui/button";
import StripeConnectBanner from "../components/StripeConnectBanner";
import { toast } from "sonner";
import {
  DollarSign,
  ArrowDownRight,
  ArrowUpRight,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Calendar,
  Building2,
  FileText,
  RefreshCw,
  ExternalLink,
  TrendingUp,
  Wallet,
  Filter,
  Download
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Format currency
const formatCurrency = (value) => {
  if (!value && value !== 0) return "$0";
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

// Format date
const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric"
  });
};

// Status badge
function StatusBadge({ status }) {
  const configs = {
    completed: { bg: "bg-emerald-500/10", border: "border-emerald-500/30", text: "text-emerald-400", icon: CheckCircle, label: "Completed" },
    pending: { bg: "bg-amber-500/10", border: "border-amber-500/30", text: "text-amber-400", icon: Clock, label: "Pending" },
    approved: { bg: "bg-blue-500/10", border: "border-blue-500/30", text: "text-blue-400", icon: CheckCircle, label: "Approved" },
    failed: { bg: "bg-red-500/10", border: "border-red-500/30", text: "text-red-400", icon: XCircle, label: "Failed" },
    pending_manual: { bg: "bg-amber-500/10", border: "border-amber-500/30", text: "text-amber-400", icon: AlertCircle, label: "Manual Transfer" }
  };
  
  const config = configs[status] || configs.pending;
  const Icon = config.icon;
  
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-1 text-xs font-medium rounded ${config.bg} ${config.border} border ${config.text}`}>
      <Icon className="w-3 h-3" />
      {config.label}
    </span>
  );
}

// Payment type label
const PAYMENT_TYPE_LABELS = {
  earnest_money: "Earnest Money",
  assignment_fee: "Assignment Fee",
  contractor_draw: "Contractor Draw",
  closing_funds: "Closing Funds",
  realtor_commission: "Commission",
  lender_fee: "Origination Fee",
  title_fee: "Title Fee",
  distribution: "Distribution",
  refund: "Refund"
};

export default function PaymentHistoryPage({ user, subscription, onLogout }) {
  const { mainClass } = useSidebar();
  const [loading, setLoading] = useState(true);
  const [payments, setPayments] = useState([]);
  const [summary, setSummary] = useState({
    total_received: 0,
    pending_amount: 0,
    this_month: 0,
    transaction_count: 0
  });
  const [stripeStatus, setStripeStatus] = useState(null);
  const [filter, setFilter] = useState("all"); // all, received, pending

  useEffect(() => {
    fetchPayments();
  }, []);

  const fetchPayments = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/payments/history`, {
        withCredentials: true
      });
      setPayments(response.data.payments || []);
      setSummary(response.data.summary || {});
    } catch (error) {
      console.error("Failed to fetch payments:", error);
      // Set empty state if API doesn't exist yet
      setPayments([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredPayments = payments.filter(p => {
    if (filter === "all") return true;
    if (filter === "received") return p.status === "completed";
    if (filter === "pending") return ["pending", "approved", "pending_manual"].includes(p.status);
    return true;
  });

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="payment-history-page">
      <Sidebar user={user} subscription={subscription} onLogout={onLogout} />
      
      <main className={mainClass}>
        {/* Header */}
        <header className="sticky top-0 z-20 bg-[#0A0A0A]/90 backdrop-blur-md border-b border-white/[0.04] px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-white flex items-center gap-3">
                <DollarSign className="w-7 h-7 text-emerald-400" />
                Payment History
              </h1>
              <p className="text-white/50 text-sm mt-1">Track all payments received through DealForge</p>
            </div>
            <Button
              onClick={fetchPayments}
              className="bg-white/5 hover:bg-white/10 text-white"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </div>
        </header>

        <div className="p-6 space-y-6">
          {/* Stripe Connect Banner */}
          <StripeConnectBanner 
            partnerType="investor" 
            apiPrefix="/api/auth"
            onStatusChange={setStripeStatus}
          />

          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-[#111111] border border-white/[0.08] rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <ArrowDownRight className="w-5 h-5 text-emerald-400" />
                <span className="text-white/50 text-xs uppercase">Total Received</span>
              </div>
              <p className="text-2xl font-bold text-emerald-400">{formatCurrency(summary.total_received)}</p>
            </div>
            
            <div className="bg-[#111111] border border-white/[0.08] rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="w-5 h-5 text-amber-400" />
                <span className="text-white/50 text-xs uppercase">Pending</span>
              </div>
              <p className="text-2xl font-bold text-amber-400">{formatCurrency(summary.pending_amount)}</p>
            </div>
            
            <div className="bg-[#111111] border border-white/[0.08] rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Calendar className="w-5 h-5 text-[#D4AF37]" />
                <span className="text-white/50 text-xs uppercase">This Month</span>
              </div>
              <p className="text-2xl font-bold text-[#D4AF37]">{formatCurrency(summary.this_month)}</p>
            </div>
            
            <div className="bg-[#111111] border border-white/[0.08] rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-5 h-5 text-blue-400" />
                <span className="text-white/50 text-xs uppercase">Transactions</span>
              </div>
              <p className="text-2xl font-bold text-white">{summary.transaction_count}</p>
            </div>
          </div>

          {/* Filter Tabs */}
          <div className="flex gap-2">
            <Button
              onClick={() => setFilter("all")}
              className={`${filter === "all" ? "bg-white/10 text-white" : "bg-transparent text-white/50 hover:text-white"}`}
            >
              All
            </Button>
            <Button
              onClick={() => setFilter("received")}
              className={`${filter === "received" ? "bg-emerald-500/20 text-emerald-400" : "bg-transparent text-white/50 hover:text-white"}`}
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              Received
            </Button>
            <Button
              onClick={() => setFilter("pending")}
              className={`${filter === "pending" ? "bg-amber-500/20 text-amber-400" : "bg-transparent text-white/50 hover:text-white"}`}
            >
              <Clock className="w-4 h-4 mr-2" />
              Pending
            </Button>
          </div>

          {/* Payments Table */}
          <div className="bg-[#111111] border border-white/[0.08] rounded-lg overflow-hidden">
            {loading ? (
              <div className="flex items-center justify-center p-12">
                <div className="w-8 h-8 border-2 border-emerald-400 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : filteredPayments.length === 0 ? (
              <div className="p-12 text-center">
                <Wallet className="w-16 h-16 text-white/10 mx-auto mb-4" />
                <p className="text-white/50">No payments found</p>
                <p className="text-white/30 text-sm mt-1">
                  {stripeStatus?.onboarding_complete 
                    ? "Complete deals to receive payments"
                    : "Connect Stripe to start receiving payments"}
                </p>
              </div>
            ) : (
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/[0.06]">
                    <th className="text-left text-white/50 text-xs font-medium p-4 uppercase">Date</th>
                    <th className="text-left text-white/50 text-xs font-medium p-4 uppercase">Type</th>
                    <th className="text-left text-white/50 text-xs font-medium p-4 uppercase">Deal/Source</th>
                    <th className="text-right text-white/50 text-xs font-medium p-4 uppercase">Amount</th>
                    <th className="text-center text-white/50 text-xs font-medium p-4 uppercase">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/[0.06]">
                  {filteredPayments.map((payment, idx) => (
                    <tr key={payment.transaction_id || idx} className="hover:bg-white/[0.02]">
                      <td className="p-4">
                        <p className="text-white text-sm">{formatDate(payment.created_at)}</p>
                      </td>
                      <td className="p-4">
                        <p className="text-white text-sm capitalize">
                          {PAYMENT_TYPE_LABELS[payment.payment_type] || payment.payment_type?.replace(/_/g, ' ')}
                        </p>
                      </td>
                      <td className="p-4">
                        <p className="text-white text-sm">{payment.property_address || payment.deal_id || "-"}</p>
                        {payment.description && (
                          <p className="text-white/40 text-xs mt-0.5">{payment.description}</p>
                        )}
                      </td>
                      <td className="p-4 text-right">
                        <p className="text-emerald-400 font-bold">{formatCurrency(payment.amount)}</p>
                        {payment.platform_fee > 0 && (
                          <p className="text-white/40 text-xs">Fee: {formatCurrency(payment.platform_fee)}</p>
                        )}
                      </td>
                      <td className="p-4 text-center">
                        <StatusBadge status={payment.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Info Section */}
          <div className="bg-[#0A0A0A] border border-white/[0.06] rounded-lg p-6">
            <h3 className="text-white font-medium mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#D4AF37]" />
              Understanding Your Payments
            </h3>
            <div className="grid md:grid-cols-3 gap-6 text-sm">
              <div>
                <h4 className="text-emerald-400 font-medium mb-2">Completed</h4>
                <p className="text-white/50">
                  Funds have been transferred to your connected Stripe account and deposited to your bank.
                </p>
              </div>
              <div>
                <h4 className="text-amber-400 font-medium mb-2">Pending</h4>
                <p className="text-white/50">
                  Payment is awaiting approval from the title company or processing by Stripe.
                </p>
              </div>
              <div>
                <h4 className="text-blue-400 font-medium mb-2">Manual Transfer</h4>
                <p className="text-white/50">
                  Connect your Stripe account to receive automatic transfers for future payments.
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
